import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown, ExternalLink, ChevronDown } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  FEATURED_CASE_STUDIES,
  PORTFOLIO_SITES,
  INDUSTRY_FILTERS,
  BUILD_FILTERS,
  BUILD_SYSTEM,
  FAQ_ITEMS,
  MEASUREMENT_DISCLOSURE,
  SNAPSHOT_LABEL,
} from "@/data/caseStudiesData";

const SITE_URL = "https://digitalmedianinja.com";
const PAGE_TITLE = "Local SEO, AEO & GEO Case Studies | Digital Media Ninja";
const PAGE_DESC =
  "Explore real website growth across legal, real estate, wellness, fitness, home services, and B2B. See how Digital Media Ninja builds for local search, answer engines, and AI discovery.";

const INK = "#14120F";
const LIME = "#CDFF3D";
const CREAM = "#FAF6EF";

const usePageHead = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    let desc = document.querySelector('meta[name="description"]');
    const createdDesc = !desc;
    if (!desc) {
      desc = document.createElement("meta");
      desc.name = "description";
      document.head.appendChild(desc);
    }
    const prevDesc = desc.getAttribute("content");
    desc.setAttribute("content", PAGE_DESC);

    let canon = document.querySelector('link[rel="canonical"]');
    const createdCanon = !canon;
    if (!canon) {
      canon = document.createElement("link");
      canon.rel = "canonical";
      document.head.appendChild(canon);
    }
    const prevCanon = canon.getAttribute("href");
    canon.setAttribute("href", `${SITE_URL}/case-studies`);

    const schema = [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Local SEO, AEO & GEO Case Studies",
        description: PAGE_DESC,
        url: `${SITE_URL}/case-studies`,
        isPartOf: { "@type": "WebSite", name: "Digital Media Ninja", url: SITE_URL },
        about: { "@type": "ProfessionalService", name: "Digital Media Ninja", "@id": `${SITE_URL}/#organization` },
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: FEATURED_CASE_STUDIES.length + PORTFOLIO_SITES.length,
          itemListElement: [...FEATURED_CASE_STUDIES, ...PORTFOLIO_SITES].map((s, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: s.domain,
            url: s.url,
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "Case Studies", item: `${SITE_URL}/case-studies` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ_ITEMS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "case-studies-jsonld";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (createdDesc) desc.remove();
      else desc.setAttribute("content", prevDesc || "");
      if (createdCanon) canon.remove();
      else canon.setAttribute("href", prevCanon || "");
      script.remove();
    };
  }, []);
};

const FilterButton = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    data-testid={testId}
    className={`px-4 py-2 text-sm font-bold border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#14120F] ${
      active
        ? "bg-[#14120F] text-[#CDFF3D] border-[#14120F]"
        : "bg-white text-[#14120F] border-[#14120F]/25 hover:border-[#14120F]"
    }`}
  >
    {children}
  </button>
);

const ScreenshotFrame = ({ site, aspect = "aspect-[16/10]", eager = false }) => (
  <a
    href={site.url}
    target="_blank"
    rel="noopener noreferrer"
    className="group/shot block border-b-2 border-[#14120F]"
    aria-label={`Visit ${site.domain} (opens in a new tab)`}
  >
    <div className="flex items-center gap-1.5 px-4 py-2 bg-[#FAF6EF] border-b-2 border-[#14120F]">
      <span className="w-2.5 h-2.5 rounded-full border border-[#14120F]/60" aria-hidden="true" />
      <span className="w-2.5 h-2.5 rounded-full border border-[#14120F]/60" aria-hidden="true" />
      <span className="w-2.5 h-2.5 rounded-full border border-[#14120F]/60" aria-hidden="true" />
      <span className="ml-3 text-[11px] font-mono text-[#14120F]/60 truncate lowercase">{site.domain}</span>
    </div>
    <div className={`relative overflow-hidden ${aspect} bg-[#EDE8DF]`}>
      <img
        src={site.screenshot}
        alt={`${site.domain} website homepage preview`}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        width="1440"
        height="900"
        className="w-full h-full object-cover object-top motion-safe:transition-transform motion-safe:duration-500 motion-safe:group-hover/shot:scale-[1.04]"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "/images/case-studies/og-case-studies.png";
        }}
      />
      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" aria-hidden="true" />
      <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-[#CDFF3D] text-[#14120F] text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 border border-[#14120F]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#14120F]" aria-hidden="true" />
        Live website
      </span>
    </div>
  </a>
);

const Chip = ({ children, dark }) => (
  <span
    className={`inline-block text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 border ${
      dark ? "bg-[#14120F] text-[#CDFF3D] border-[#14120F]" : "bg-[#FAF6EF] text-[#14120F] border-[#14120F]/30"
    }`}
  >
    {children}
  </span>
);

const FeaturedCard = ({ study, index }) => (
  <article
    className="bg-white border-2 border-[#14120F] shadow-[6px_6px_0_#14120F] flex flex-col"
    data-testid={`featured-card-${study.slug}`}
  >
    <ScreenshotFrame site={study} eager={index < 2} />
    <div className="p-6 sm:p-7 flex flex-col flex-1">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Chip dark>{study.category}</Chip>
        <Chip>{study.buildType}</Chip>
      </div>
      <h3 className="text-2xl font-extrabold text-[#14120F] tracking-tight mb-2">{study.domain}</h3>
      <p className="text-[#5B564C] leading-relaxed mb-6">{study.description}</p>

      <div className="grid grid-cols-2 gap-px bg-[#14120F] border-2 border-[#14120F] mb-5">
        {study.metrics.map((m) => (
          <div key={m.label} className="bg-white p-3.5">
            <p className="text-xl sm:text-2xl font-extrabold text-[#14120F] leading-none mb-1.5">{m.value}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A8377]">{m.label}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-[#8A8377] mb-5">{SNAPSHOT_LABEL}</p>

      <div className="flex flex-wrap gap-1.5 mb-6">
        {study.services.map((s) => (
          <span key={s} className="text-xs text-[#14120F]/80 bg-[#FAF6EF] border border-[#14120F]/15 px-2 py-0.5">
            {s}
          </span>
        ))}
      </div>

      <details className="mb-6 group/details" data-testid={`case-details-${study.slug}`}>
        <summary className="cursor-pointer list-none inline-flex items-center gap-1.5 text-sm font-bold text-[#14120F] underline decoration-[#CDFF3D] decoration-[3px] underline-offset-4 hover:decoration-[#14120F]">
          View case study
          <ChevronDown className="w-4 h-4 motion-safe:transition-transform group-open/details:rotate-180" aria-hidden="true" />
        </summary>
        <p className="mt-3 text-sm text-[#5B564C] leading-relaxed border-l-4 border-[#CDFF3D] pl-4">{study.details}</p>
      </details>

      <div className="mt-auto flex flex-wrap items-center gap-3">
        <a
          href={study.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`visit-site-${study.slug}`}
          className="inline-flex items-center gap-2 text-sm font-extrabold text-[#14120F] bg-[#CDFF3D] border-2 border-[#14120F] px-4 py-2.5 shadow-[3px_3px_0_#14120F] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-[box-shadow,transform]"
        >
          Visit live site <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
        </a>
        {study.caseStudyLink && (
          <Link
            to={study.caseStudyLink}
            data-testid={`featured-case-study-link-${study.slug}`}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#14120F] underline decoration-[#CDFF3D] decoration-[3px] underline-offset-4 hover:decoration-[#14120F]"
          >
            Read the full case study <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
          </Link>
        )}
      </div>
    </div>
  </article>
);

const PortfolioCard = ({ site }) => {
  const metric = (v) => (v != null ? v : <span className="text-[#8A8377] font-semibold text-sm">Collecting data</span>);
  return (
    <article
      className="bg-white border-2 border-[#14120F] shadow-[4px_4px_0_#14120F] flex flex-col"
      data-testid={`portfolio-card-${site.slug}`}
    >
      <ScreenshotFrame site={site} aspect="aspect-[3/2]" />
      <div className="p-5 flex flex-col flex-1">
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          <Chip dark>{site.industry}</Chip>
          <Chip>{site.buildType}</Chip>
        </div>
        <h3 className="text-lg font-extrabold text-[#14120F] tracking-tight mb-4">{site.domain}</h3>
        <dl className="space-y-2 mb-5 text-sm">
          <div className="flex items-baseline justify-between gap-3 border-b border-[#14120F]/10 pb-2">
            <dt className="text-[#8A8377] text-xs font-bold uppercase tracking-wider">Ranking keywords</dt>
            <dd className="font-extrabold text-[#14120F]">{metric(site.keywords)}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-3 border-b border-[#14120F]/10 pb-2">
            <dt className="text-[#8A8377] text-xs font-bold uppercase tracking-wider">Est. organic traffic</dt>
            <dd className="font-extrabold text-[#14120F]">{metric(site.traffic)}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-[#8A8377] text-xs font-bold uppercase tracking-wider">Local-pack SERPs</dt>
            <dd className="font-extrabold text-[#14120F]">{metric(site.localPack)}</dd>
          </div>
        </dl>
        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2">
          <a
            href={site.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`visit-site-${site.slug}`}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#14120F] underline decoration-[#CDFF3D] decoration-[3px] underline-offset-4 hover:decoration-[#14120F]"
          >
            Live website <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
          </a>
          {site.caseStudyLink && (
            <Link
              to={site.caseStudyLink}
              data-testid={`case-study-link-${site.slug}`}
              className="inline-flex items-center gap-1.5 text-sm font-bold text-[#14120F] underline decoration-[#CDFF3D] decoration-[3px] underline-offset-4 hover:decoration-[#14120F]"
            >
              View case study <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
            </Link>
          )}
        </div>
      </div>
    </article>
  );
};

const CaseStudiesPage = () => {
  usePageHead();
  const [industry, setIndustry] = useState("all");
  const [buildType, setBuildType] = useState("all");

  const matches = (item) =>
    (industry === "all" || item.filterCategory === industry) &&
    (buildType === "all" || item.buildType === (buildType === "new-build" ? "New Build" : "Relaunch"));

  const featured = useMemo(() => FEATURED_CASE_STUDIES.filter(matches), [industry, buildType]);
  const portfolio = useMemo(() => PORTFOLIO_SITES.filter(matches), [industry, buildType]);
  const total = featured.length + portfolio.length;

  const scrollToResults = () => {
    document.getElementById("results")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-[#FAF6EF] text-[#14120F]" data-testid="case-studies-page">
      {/* ============ HERO ============ */}
      <header className="pt-32 pb-16 sm:pb-20 border-b-2 border-[#14120F] overflow-hidden">
        <div className="container-custom">
          <nav aria-label="Breadcrumb" className="mb-8">
            <ol className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#8A8377]">
              <li>
                <Link to="/" className="hover:text-[#14120F]">Home</Link>
              </li>
              <li aria-hidden="true">/</li>
              <li aria-current="page" className="text-[#14120F]">Case Studies</li>
            </ol>
          </nav>

          <p className="inline-block bg-[#14120F] text-[#CDFF3D] text-xs font-extrabold uppercase tracking-widest px-3 py-1.5 mb-8">
            Independent Search Visibility Study · October 2026
          </p>

          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-6">
              We build websites that{" "}
              <span className="bg-[#CDFF3D] px-2 -mx-1 box-decoration-clone">get found.</span>
            </h1>
            <p className="text-base md:text-lg text-[#5B564C] leading-relaxed max-w-2xl mb-10">
              New builds and strategic relaunches engineered for local search, answer engines, and AI
              discovery—from schema and content to conversion.
            </p>
            <div className="flex flex-wrap gap-4 mb-14">
              <button
                type="button"
                onClick={scrollToResults}
                data-testid="hero-explore-results-btn"
                className="inline-flex items-center gap-2 text-sm sm:text-base font-extrabold text-[#14120F] bg-[#CDFF3D] border-2 border-[#14120F] px-6 py-3.5 shadow-[4px_4px_0_#14120F] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-[box-shadow,transform]"
              >
                Explore the results <ArrowDown className="w-4 h-4" aria-hidden="true" />
              </button>
              <Link
                to="/contact"
                data-testid="hero-book-call-btn"
                className="inline-flex items-center gap-2 text-sm sm:text-base font-extrabold text-[#FAF6EF] bg-[#14120F] border-2 border-[#14120F] px-6 py-3.5 shadow-[4px_4px_0_#CDFF3D] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-[box-shadow,transform]"
              >
                Book a strategy call <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>

          <dl className="grid grid-cols-2 lg:grid-cols-4 border-2 border-[#14120F] bg-[#14120F] gap-px" data-testid="hero-snapshot-metrics">
            {[
              { value: "8,457", label: "Ranking keywords" },
              { value: "3,865", label: "Est. monthly organic visits" },
              { value: "15", label: "Domains with measurable visibility" },
              { value: "6", label: "Industries represented" },
            ].map((s) => (
              <div key={s.label} className="bg-[#FAF6EF] p-5 sm:p-6">
                <dd className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">{s.value}</dd>
                <dt className="text-xs font-bold uppercase tracking-wider text-[#8A8377]">{s.label}</dt>
              </div>
            ))}
          </dl>
        </div>
      </header>

      {/* ============ FILTERS + RESULTS ============ */}
      <section id="results" className="py-16 sm:py-24 scroll-mt-24" aria-labelledby="results-heading">
        <div className="container-custom">
          <h2 id="results-heading" className="text-base md:text-lg font-extrabold uppercase tracking-widest mb-8">
            <span className="bg-[#CDFF3D] px-2 py-1 border-2 border-[#14120F]">The results</span>
          </h2>

          <div className="mb-4">
            <p className="text-xs font-bold uppercase tracking-widest text-[#8A8377] mb-2">Filter by industry</p>
            <div role="group" aria-label="Filter case studies by industry" className="flex flex-wrap gap-2">
              {INDUSTRY_FILTERS.map((f) => (
                <FilterButton
                  key={f.key}
                  active={industry === f.key}
                  onClick={() => setIndustry(f.key)}
                  testId={`filter-industry-${f.key}`}
                >
                  {f.label}
                </FilterButton>
              ))}
            </div>
          </div>
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-widest text-[#8A8377] mb-2">Filter by build type</p>
            <div role="group" aria-label="Filter case studies by build type" className="flex flex-wrap gap-2">
              {BUILD_FILTERS.map((f) => (
                <FilterButton
                  key={f.key}
                  active={buildType === f.key}
                  onClick={() => setBuildType(f.key)}
                  testId={`filter-build-${f.key}`}
                >
                  {f.label}
                </FilterButton>
              ))}
            </div>
          </div>

          <p aria-live="polite" className="text-sm text-[#8A8377] font-semibold mb-10" data-testid="filter-results-count">
            Showing {total} of {FEATURED_CASE_STUDIES.length + PORTFOLIO_SITES.length} websites
          </p>

          {industry === "fitness-sports" && (
            <p className="mb-10 -mt-6 text-sm font-bold" data-testid="fitness-portfolio-link">
              <Link to="/gym-website-design" className="underline decoration-[#CDFF3D] decoration-[3px] underline-offset-4 hover:decoration-[#14120F]">
                See every gym & fitness site we've built, with before/after SEO numbers →
              </Link>
            </p>
          )}

          {total === 0 && (
            <div className="border-2 border-dashed border-[#14120F]/30 p-12 text-center mb-16" data-testid="filter-empty-state">
              <p className="font-extrabold text-lg mb-2">No case studies match this combination.</p>
              <p className="text-[#5B564C] text-sm">Try a different industry or build type.</p>
            </div>
          )}

          {featured.length > 0 && (
            <>
              <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-8">Featured case studies</h3>
              <div className="grid md:grid-cols-2 gap-8 lg:gap-10 mb-20">
                {featured.map((study, i) => (
                  <FeaturedCard key={study.slug} study={study} index={i} />
                ))}
              </div>
            </>
          )}

          {portfolio.length > 0 && (
            <>
              <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3">Additional website portfolio</h3>
              <p className="text-[#5B564C] mb-8 max-w-2xl">
                More live builds with measurable local visibility. Where Semrush hasn't collected enough
                information yet, metrics are marked “Collecting data.”
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {portfolio.map((site) => (
                  <PortfolioCard key={site.slug} site={site} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ============ BUILD SYSTEM ============ */}
      <section className="py-16 sm:py-24 bg-[#14120F] text-[#FAF6EF] border-y-2 border-[#14120F]" aria-labelledby="system-heading">
        <div className="container-custom">
          <h2 id="system-heading" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4 max-w-3xl">
            Built for the way search works{" "}
            <span className="text-[#CDFF3D]">now.</span>
          </h2>
          <p className="text-[#B5AFa3] text-base md:text-lg max-w-2xl mb-14" style={{ color: "#B5AFA3" }}>
            Every website in this study runs on the same six-part Digital Media Ninja system.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#FAF6EF]/20 border border-[#FAF6EF]/20">
            {BUILD_SYSTEM.map((item) => (
              <div key={item.num} className="bg-[#14120F] p-7" data-testid={`system-item-${item.num}`}>
                <p className="text-[#CDFF3D] font-mono font-bold text-sm mb-3">{item.num}</p>
                <h3 className="text-xl font-extrabold mb-3">{item.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "#B5AFA3" }}>{item.copy}</p>
                {item.tags && (
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {item.tags.map((t) => (
                      <span key={t} className="text-[11px] font-mono text-[#CDFF3D] border border-[#CDFF3D]/40 px-1.5 py-0.5">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-bold">
            <span className="text-[#FAF6EF]/60 uppercase tracking-widest text-xs">Explore the services behind these results:</span>
            {[
              { name: "Local SEO", path: "/services/local-seo" },
              { name: "AEO", path: "/services/aeo" },
              { name: "GEO", path: "/services/geo" },
              { name: "LLM Optimization", path: "/services/llm-optimization" },
              { name: "Web Design", path: "/services/web-design" },
              { name: "Content Marketing", path: "/services/content-marketing" },
              { name: "GBP Optimization", path: "/services/gbp-optimization" },
            ].map((s) => (
              <Link
                key={s.path + s.name}
                to={s.path}
                className="text-[#CDFF3D] underline decoration-2 underline-offset-4 hover:text-[#FAF6EF]"
              >
                {s.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ MEASUREMENT DISCLOSURE ============ */}
      <section className="py-14 sm:py-16" aria-labelledby="methodology-heading">
        <div className="container-custom">
          <div className="border-2 border-[#14120F] bg-white p-7 sm:p-9 shadow-[6px_6px_0_#CDFF3D]" data-testid="measurement-disclosure">
            <h2 id="methodology-heading" className="text-base md:text-lg font-extrabold uppercase tracking-widest mb-4">
              Measurement methodology
            </h2>
            <p className="text-[#5B564C] leading-relaxed text-sm sm:text-base">{MEASUREMENT_DISCLOSURE}</p>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="py-16 sm:py-24" aria-labelledby="faq-heading">
        <div className="container-custom">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <h2 id="faq-heading" className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
                Questions, <span className="bg-[#CDFF3D] px-1.5 box-decoration-clone">answered.</span>
              </h2>
              <p className="text-[#5B564C]">
                The things business owners ask before hiring us for a new build or relaunch.
              </p>
            </div>
            <div className="lg:col-span-8">
              <Accordion type="single" collapsible className="border-2 border-[#14120F] bg-white divide-y-2 divide-[#14120F]" data-testid="case-studies-faq">
                {FAQ_ITEMS.map((item, i) => (
                  <AccordionItem key={item.q} value={`faq-${i}`} className="border-b-0 px-6">
                    <AccordionTrigger
                      className="text-left font-extrabold text-[#14120F] hover:no-underline py-5"
                      data-testid={`faq-trigger-${i}`}
                    >
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#5B564C] leading-relaxed text-sm sm:text-base pb-6">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CONVERSION CTA ============ */}
      <section className="py-16 sm:py-24 bg-[#CDFF3D] border-t-2 border-[#14120F]" aria-labelledby="cta-heading">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h2 id="cta-heading" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-5">
              Your website should be your best salesperson.
            </h2>
            <p className="text-[#14120F]/75 text-base md:text-lg leading-relaxed mb-9">
              Let's build the search, answer, and AI visibility system your next stage of growth needs.
            </p>
            <Link
              to="/contact"
              data-testid="cta-book-strategy-call-btn"
              className="inline-flex items-center gap-2 text-base font-extrabold text-[#CDFF3D] bg-[#14120F] border-2 border-[#14120F] px-8 py-4 shadow-[5px_5px_0_#FAF6EF] hover:shadow-none hover:translate-x-[5px] hover:translate-y-[5px] transition-[box-shadow,transform]"
            >
              Book a strategy call <ArrowUpRight className="w-5 h-5" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
